import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EVENTS, PRACTICE_PLAYERS, codeSchema } from '@sh/shared'
import { request } from '../net/socket.js'
import { recallLobby, recallName, rememberName, saveSeat } from '../net/session.js'
import { Attribution } from '../components/Attribution.js'
import { Feedback } from '../components/Feedback.js'
import { useSkin } from '../skin/skin.js'
import { assetUrl } from '../skin/skin.js'
import { useSkinCatalog } from '../skin/catalog.js'

/**
 * Five ways in, but only two tabs. Nearly everybody here is starting a table
 * or joining one, so those are the tabs; the rest are offered where they are
 * needed rather than all at once.
 *
 * `board` and `claim` both start from a lobby code, so they live under Join,
 * as lines inside its form. `board` is the odd one: it claims nothing, holds
 * nothing, and only ever sees what the table can already see. `claim` is
 * mostly reached without asking - a refused join brings up the PIN on its own.
 *
 * `practice` is for the person with nobody to play with yet, offered beside
 * /learn as a way round the tabs rather than as one of them.
 */
type Mode = 'create' | 'join' | 'claim' | 'board' | 'practice'

const TABS = ['create', 'join'] as const

const TAB_LABEL: Record<(typeof TABS)[number], string> = {
  create: 'New game',
  join: 'Join',
}

/** The tab a mode sits under, if it sits under one. */
const TAB_OF: Record<Mode, (typeof TABS)[number] | null> = {
  create: 'create',
  join: 'join',
  claim: 'join',
  board: 'join',
  practice: null,
}

/** The ways to use a lobby code, each offered from the other two. */
const CODE_LINK: Partial<Record<Mode, string>> = {
  join: 'Join as a player',
  board: 'Open it as the table board',
  claim: 'I have a claim PIN',
}

const SUBMIT_LABEL: Record<Mode, string> = {
  create: 'Create lobby',
  join: 'Join lobby',
  claim: 'Reclaim my seat',
  board: 'Open the board',
  practice: 'Start practice game',
}

export function Home() {
  const params = useParams<{ code?: string }>()
  const navigate = useNavigate()
  const [name, setName] = useState(recallName())
  const [code, setCode] = useState((params.code ?? '').toUpperCase())
  const [pin, setPin] = useState('')
  // `?mode=practice` is how a link from elsewhere - the end of /learn - lands
  // somebody straight in front of the practice form.
  const [search] = useSearchParams()
  const [mode, setMode] = useState<Mode>(
    params.code ? 'join' : search.get('mode') === 'practice' ? 'practice' : 'create',
  )
  const [players, setPlayers] = useState<number>(PRACTICE_PLAYERS.default)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /*
   * Set when a join is refused because the name belongs to a seat at that
   * table whose phone has gone quiet - which is nearly always the person who
   * was sitting in it, back on something else. While it is set this screen
   * stops being four ways in and becomes one: type the PIN for that seat.
   *
   * It holds the table's own spelling of the name rather than whatever was
   * typed, so the sentence below names the seat the host is looking at.
   */
  const [reclaim, setReclaim] = useState<{ code: string; name: string } | null>(null)
  const codeInput = useRef<HTMLInputElement>(null)
  const pinInput = useRef<HTMLInputElement>(null)
  /*
   * Read once, on mount: nothing on this screen changes where this device was
   * last seated, and taking a seat below navigates away from it.
   */
  const [recent] = useState(() => recallLobby())
  const focusCode = useRef(false)
  // The landing page belongs to no lobby yet, so it shows whichever skin a new
  // lobby will start on - and nothing at all when this deployment has no skins,
  // where the wordmark below stands in for the logo.
  const catalog = useSkinCatalog()
  const logo = assetUrl(useSkin(catalog.skins.length > 0 ? catalog.default : null), 'logo')

  const pick = (m: Mode) => {
    setMode(m)
    setError(null)
    // Choosing a way in is choosing one, so the PIN prompt steps aside. It is
    // how somebody who is not that player gets back to a name field.
    setReclaim(null)
  }

  /*
   * Sending someone to a tab is only half of it; they still have to find the
   * one box it asks them to fill. The code field does not exist yet at the
   * moment the line below is clicked - the field may not be rendered yet - so
   * the focus waits here for the tab to have actually arrived.
   */
  useEffect(() => {
    if (!focusCode.current) return
    focusCode.current = false
    codeInput.current?.focus()
  }, [mode])

  /*
   * And the same for the PIN box, which arrives without anybody having asked
   * for it. `autoFocus` will not do: it fires when a node is created, and the
   * two sets of fields below reconcile slot by slot - the box that appears is
   * the one that was the lobby code a moment ago, updated in place rather than
   * made. The wrapper around the prompt is what keeps that from happening; this
   * does not depend on it having worked.
   */
  useEffect(() => {
    if (reclaim) pinInput.current?.focus()
  }, [reclaim])

  /**
   * Takes a seat at `lobby` and goes there, or puts the reason on screen.
   *
   * Answers whether it got in rather than with the reason, because the reason
   * has two shapes now - a line of red, or the PIN prompt - and both callers
   * want the same thing out of a refusal: to know that one happened.
   */
  const joinLobby = async (lobby: string, who: string): Promise<boolean> => {
    const ack = await request<{ code: string; playerId: string; token: string }>(EVENTS.join, {
      code: lobby,
      name: who,
    })
    if (!ack.ok) {
      // The seat is still theirs and the table knows it; all that is missing is
      // the PIN. Ask for it here, rather than leave them to discover that a tab
      // called Reclaim seat exists and is the one they want.
      if (ack.reclaim) setReclaim({ code: lobby, name: ack.reclaim.name })
      else setError(ack.error)
      return false
    }
    rememberName(who)
    saveSeat(ack.data)
    navigate(`/play/${ack.data.code}`)
    return true
  }

  const go = async () => {
    setBusy(true)
    setError(null)
    try {
      /*
       * The PIN prompt owns the form while it is up, whichever tab is lit
       * behind it. The code is the one that was refused rather than whatever
       * is in the box, so editing the field cannot aim the PIN at a different
       * table than the seat it was issued for.
       */
      if (reclaim) {
        const ack = await request<{ code: string; playerId: string; token: string }>(EVENTS.claim, {
          code: reclaim.code,
          pin,
        })
        if (!ack.ok) return setError(ack.error)
        // The table's spelling, now that this device has proved the seat is
        // its own: it is the name the next join from this browser offers.
        rememberName(reclaim.name)
        saveSeat(ack.data)
        navigate(`/play/${ack.data.code}`)
        return
      }

      if (mode === 'create' || mode === 'practice') {
        const ack = await request<{ code: string; playerId: string; token: string }>(
          mode === 'practice' ? EVENTS.practice : EVENTS.create,
          mode === 'practice' ? { name, players } : { name },
        )
        if (!ack.ok) return setError(ack.error)
        rememberName(name)
        saveSeat(ack.data)
        navigate(`/play/${ack.data.code}`)
        return
      }

      const parsed = codeSchema.safeParse(code)
      if (!parsed.success) return setError('that is not a valid four character code')

      // A table screen authenticates with the lobby code and nothing else, so
      // there is nothing to ask the server for here - and no seat, no name and
      // no token to keep. Whether that lobby exists is the board screen's own
      // question to answer, in letters a whole room can read.
      if (mode === 'board') {
        navigate(`/board/${parsed.data}`)
        return
      }

      if (mode === 'claim') {
        const ack = await request<{ code: string; playerId: string; token: string }>(EVENTS.claim, {
          code: parsed.data,
          pin,
        })
        if (!ack.ok) return setError(ack.error)
        saveSeat(ack.data)
        navigate(`/play/${ack.data.code}`)
        return
      }

      await joinLobby(parsed.data, name)
    } finally {
      setBusy(false)
    }
  }

  /**
   * One tap back to the table this device just left. It is a whole join - the
   * seat was handed back, and the table has been going on without it - so the
   * name is the one this browser last played under, and anything the table
   * will not accept (a code that has since gone, a name somebody else took in
   * the meantime) drops into the Join tab with the reason, which is where it
   * can be fixed.
   */
  const rejoin = async (lobby: string) => {
    setCode(lobby)
    if (!name.trim()) return pick('join')
    setBusy(true)
    setError(null)
    try {
      // `setMode` rather than `pick`: the reason is already on screen by now,
      // and `pick` is the control that clears one.
      if (!(await joinLobby(lobby, name))) setMode('join')
    } finally {
      setBusy(false)
    }
  }

  const ready = reclaim
    ? /^\d{4}$/.test(pin)
    : mode === 'claim'
      ? code.length === 4 && /^\d{4}$/.test(pin)
      : mode === 'board'
        ? code.length === 4
        : name.trim().length > 0 && (mode === 'create' || mode === 'practice' || code.length === 4)

  return (
    <main className="home">
      <header className="home__head">
        {logo ? (
          <img className="home__logo" src={logo} alt="Secret Hitler" draggable={false} />
        ) : (
          <h1 className="home__title">Secret Hitler</h1>
        )}
      </header>

      {/*
        The table this device was last at, offered back for as long as it can
        still be there. Two ways to arrive here holding one: mid-game, off a
        reopened browser or a restored tab, where the seat is still yours and
        the way back is to walk in; or having just handed the seat back, where
        it is to join again. Either way the point is the same - nobody should
        have to remember four characters they never typed.
      */}
      {recent &&
        (recent.seated ? (
          <Link className="home__resume" to={`/play/${recent.code}`} data-testid="home-resume">
            Back to your game on <strong>{recent.code}</strong>
          </Link>
        ) : (
          <button
            type="button"
            className="home__resume"
            onClick={() => void rejoin(recent.code)}
            disabled={busy}
            data-testid="home-rejoin"
          >
            Rejoin the table on <strong>{recent.code}</strong>
          </button>
        ))}

      <div className="tabs" role="tablist">
        {TABS.map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={TAB_OF[mode] === m}
            className={`tabs__tab ${TAB_OF[mode] === m ? 'is-active' : ''}`}
            onClick={() => pick(m)}
            data-testid={`tab-${m}`}
          >
            {TAB_LABEL[m]}
          </button>
        ))}
      </div>

      <form
        className="card form"
        onSubmit={(e) => {
          e.preventDefault()
          if (ready && !busy) void go()
        }}
      >
        {/*
          The PIN prompt, in place of the fields rather than beside them. By
          the time it is up there is exactly one thing left to do and it is
          type four digits; a name box and a code box still sitting above it
          would only invite the typing that already failed.
        */}
        {reclaim ? (
          <div className="reclaim">
            <p className="reclaim__note" data-testid="reclaim-note">
              <strong>{reclaim.name}</strong> already has a seat at{' '}
              <strong className="reclaim__code">{reclaim.code}</strong>, on a device that has gone
              quiet. If that was you, ask the host to tap <strong>host</strong> on their phone and
              issue a claim PIN for <strong>{reclaim.name}</strong>, then type it here. Your role
              and everything else about that seat come back with it.
            </p>
            <label className="field">
              <span className="field__label">Claim PIN</span>
              <input
                className="field__input field__input--code"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                inputMode="numeric"
                maxLength={4}
                placeholder="0000"
                ref={pinInput}
                data-testid="input-pin"
              />
            </label>
          </div>
        ) : (
          <>
          {(mode === 'create' || mode === 'join' || mode === 'practice') && (
            <label className="field">
              <span className="field__label">Your name</span>
              <input
                className="field__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={20}
                autoComplete="nickname"
                placeholder="how the table knows you"
                data-testid="input-name"
              />
            </label>
          )}

          {mode === 'practice' && (
            <div className="field" role="group" aria-labelledby="practice-size">
              <span className="field__label" id="practice-size">
                Players at the table
              </span>
              <div className="sizepick">
                {Array.from(
                  { length: PRACTICE_PLAYERS.max - PRACTICE_PLAYERS.min + 1 },
                  (_, i) => PRACTICE_PLAYERS.min + i,
                ).map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`btn btn--small sizepick__n ${n === players ? 'is-selected' : ''}`}
                    aria-pressed={n === players}
                    onClick={() => setPlayers(n)}
                    data-testid={`practice-size-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <span className="field__hint">
                You and {players - 1} bots. The bots play their own roles and never see yours.
              </span>
            </div>
          )}

          {mode !== 'create' && mode !== 'practice' && (
            <label className="field">
              <span className="field__label">Lobby code</span>
              <input
                ref={codeInput}
                className="field__input field__input--code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 4))}
                maxLength={4}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                placeholder="ABCD"
                data-testid="input-code"
              />
              {mode === 'board' && (
                <span className="field__hint" data-testid="board-blurb">
                  Shows the shared board for a game already running — the tracks, the placards, who
                  the table is waiting on. No roles, no cards, no votes: nothing a phone keeps
                  private. Open it on as many laptops or TVs as you like, all watching the same game.
                </span>
              )}
            </label>
          )}

          {mode === 'claim' && (
            <label className="field">
              <span className="field__label">Claim PIN</span>
              <input
                className="field__input field__input--code"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                inputMode="numeric"
                maxLength={4}
                placeholder="0000"
                data-testid="input-pin"
              />
              <span className="field__hint">
                Ask the host to issue a PIN for your seat, then type it here.
              </span>
            </label>
          )}
          </>
        )}

        {error && (
          <p className="error" role="alert" data-testid="home-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="btn btn--primary btn--block"
          disabled={!ready || busy}
          data-testid="submit"
        >
          {busy ? 'one moment…' : reclaim ? 'Take my seat back' : SUBMIT_LABEL[mode]}
        </button>

        {/*
          The other things a lobby code is good for, one line under the form
          that asks for it. Each mode offers the other two, so nobody who
          followed one of these is left without a way back.
        */}
        {!reclaim && TAB_OF[mode] === 'join' && (
          <p className="home__codeways">
            {(Object.keys(CODE_LINK) as Mode[])
              .filter((m) => m !== mode)
              .map((m) => (
                <button
                  key={m}
                  type="button"
                  className="home__jump"
                  onClick={() => {
                    focusCode.current = true
                    pick(m)
                  }}
                  data-testid={`home-${m}`}
                >
                  {CODE_LINK[m]}
                </button>
              ))}
          </p>
        )}

        {/*
          The way out, for the one case this guesses wrong: two people at the
          same table who answer to the same name. A tab above does the same
          thing, but it is a tab, and nothing up there says it is about the
          sentence sitting in front of you.
        */}
        {reclaim && (
          <button
            type="button"
            className="home__jump reclaim__back"
            onClick={() => {
              setPin('')
              // `pick` is what clears the prompt; this only says where to land.
              pick('join')
            }}
            data-testid="reclaim-cancel"
          >
            Not {reclaim.name}? Join under another name
          </button>
        )}
      </form>

      {/*
        First under the form, for the visitor who arrived alone and does not
        know the game yet: a two-minute tour of it, or a whole game against bots,
        before anything is asked of them.
      */}
      <p className="home__alone">
        On your own?{' '}
        <Link to="/learn" data-testid="home-learn">
          Learn to play in two minutes
        </Link>
        {mode === 'practice' ? (
          '.'
        ) : (
          <>
            , or{' '}
            <button
              type="button"
              className="home__jump"
              onClick={() => pick('practice')}
              data-testid="home-practice"
            >
              practice against bots
            </button>
            .
          </>
        )}
      </p>

      {/*
        The pages a search engine can reach. Everything else here is a form, so
        without these the writing on this site is orphaned - reachable only by
        typing the URL, which is to say not reachable.

        The last is not a page and never will be: it opens over this one,
        which is why it is a button sitting in a row of links.
      */}
      <nav className="home__links" aria-label="About the game">
        <Link to="/rules" data-testid="home-rules">
          How to play
        </Link>
        <Link to="/faq">Questions</Link>
        <Link to="/play-with/5">Playing with five</Link>
        <Feedback />
      </nav>

      {/*
        The licence asks for the credit, not for it to be the loudest thing on
        the front page: one line, which opens to the whole of it. It is in the
        document either way, folded or not.
      */}
      <details className="home__credits">
        <summary data-testid="home-credits">
          made by L4UZ · based on Secret Hitler, CC BY-NC-SA 4.0 · credits
        </summary>
        <Attribution />
      </details>
    </main>
  )
}
